import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  isValidElement,
  cloneElement,
  forwardRef,
  type ReactNode,
  type ComponentType,
  type CSSProperties,
  type HTMLAttributes,
  type ButtonHTMLAttributes,
} from 'react';
import { useTheme } from '@vitness/theme';
import { PanelLeft, ChevronRight, type LucideProps } from '@vitness/icons';
import { vp } from '@vitness/tokens';
import { Tooltip } from './Tooltip.js';
import { useBreakpoint } from '../hooks/useBreakpoint.js';

/**
 * Vitness-styled adaptation of the shadcn Sidebar composition API.
 *
 * Mirrors shadcn's part vocabulary (`SidebarProvider`, `SidebarShell`,
 * `SidebarTrigger`, `SidebarContent`, `SidebarGroup`, `SidebarMenu`,
 * `SidebarMenuButton`, `SidebarRail`, `useSidebar`) so the mental model and
 * docs map cleanly onto the wider ecosystem, but every visual is driven by the
 * Vitness token layer (`--vn-*` variables + the theme accent) to match the
 * existing custom {@link Sidebar} look (surface-raised panel, accent active
 * state, mono uppercase group labels, 6px radii).
 *
 * The shadcn reference depends on `@radix-ui/react-slot`; Vitness keeps the
 * Radix surface out of `@vitness/layouts` (lint boundary + zero Radix deps),
 * so `asChild` is satisfied by the tiny internal {@link Slot} below. Collapsed
 * icon-mode tooltips reuse the existing layouts {@link Tooltip} rather than
 * pulling in `@radix-ui/react-tooltip`.
 */

// Mirrors the system-design reference rail (`--vn-rail-w`) and the tuned legacy
// Sidebar so both navigation systems read identically.
const EXPANDED_WIDTH = 238;
const ICON_WIDTH = 64;
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

/**
 * Persist the desktop collapse state per app origin so it survives navigation
 * between shells (e.g. platform root ↔ a product console mount their own
 * provider) and page reloads. Uncontrolled providers only.
 */
const SIDEBAR_STORAGE_KEY = 'vitness:sidebar:open';

function readStoredOpen(fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored === null ? fallback : stored === '1';
  } catch {
    return fallback;
  }
}

function writeStoredOpen(open: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, open ? '1' : '0');
  } catch {
    /* storage unavailable (private mode / disabled) — non-fatal */
  }
}

type SidebarState = 'expanded' | 'collapsed';

interface SidebarContextValue {
  open: boolean;
  state: SidebarState;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /**
   * True below the *tablet* tier — the rail becomes an off-canvas drawer and
   * the shell grows a bottom nav. Not "below desktop": the 768–1023 band gets
   * the icon rail instead, which is what {@link isTablet} is for.
   */
  isMobile: boolean;
  /**
   * True in the 768–1023 band. The rail is inline as on a desktop, but opens in
   * icon mode and keeps its expand/collapse state session-local — there isn't
   * room for 238px of navigation beside content laid out for a monitor, and a
   * visit here shouldn't rewrite the monitor's preference either way.
   */
  isTablet: boolean;
  /** Whether the mobile drawer is open. */
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  accentSafe: string;
  accentIcons: boolean;
  rs: string;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

/**
 * Reads the nearest sidebar context. Throws when used outside a
 * {@link SidebarProvider} so misuse fails loudly during development.
 */
export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return ctx;
}

export interface SidebarProviderProps {
  children: ReactNode;
  /** Initial expanded state for the uncontrolled case. */
  defaultOpen?: boolean;
  /** Controlled expanded state. When provided, the provider stops owning state. */
  open?: boolean;
  /** Fired whenever the open state changes (controlled or uncontrolled). */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Owns sidebar expand/collapse state, derives the accent palette from the
 * active theme, and wires the global Ctrl/Cmd+B keyboard shortcut. Supports
 * both controlled (`open`) and uncontrolled (`defaultOpen`) usage.
 */
export function SidebarProvider({
  children,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
}: SidebarProviderProps) {
  const { accentSafe, accentRgb, accentIcons } = useTheme();
  const rs = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;
  const { isMobile, isTablet } = useBreakpoint();

  // Seed uncontrolled state from the persisted preference (falls back to defaultOpen).
  const [internalOpen, setInternalOpen] = useState(() => readStoredOpen(defaultOpen));
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const [openMobile, setOpenMobile] = useState(false);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next);
        writeStoredOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  /*
   * Tablet (768–1023) runs the rail in icon mode by default and does not write
   * its choice back.
   *
   * There is room for a rail in this band but not for 238px of one: at 768px an
   * expanded rail leaves 530px for content that was laid out expecting a
   * desktop. So the band opens in icon mode whatever the stored desktop
   * preference says — that preference is about a monitor, and this isn't one.
   * The trigger still expands it, because "start narrow" is a default and not a
   * lock; the state is just session-local and resets on the way out of the band,
   * so a tablet visit can't quietly redefine how the rail looks on a desktop.
   */
  const [tabletOpen, setTabletOpen] = useState(false);
  useEffect(() => {
    if (!isTablet && tabletOpen) setTabletOpen(false);
  }, [isTablet, tabletOpen]);

  const railOpen = isTablet ? tabletOpen : open;

  // Ctrl/Cmd+B and the trigger toggle the drawer on mobile, the rail otherwise.
  const toggle = useCallback(() => {
    if (isMobile) setOpenMobile(!openMobile);
    else if (isTablet) setTabletOpen(prev => !prev);
    else setOpen(!open);
  }, [isMobile, isTablet, openMobile, open, setOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === SIDEBAR_KEYBOARD_SHORTCUT
        && (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  // Close the drawer when leaving mobile, so it can't linger as a desktop overlay.
  useEffect(() => {
    if (!isMobile && openMobile) setOpenMobile(false);
  }, [isMobile, openMobile]);

  const value = useMemo<SidebarContextValue>(
    () => ({
      // `railOpen`, not `open` — on tablet the session-local state is the one
      // that describes what's on screen, and that's what a consumer means.
      open: railOpen,
      // The mobile drawer is always full-width, so children render expanded.
      state: isMobile ? 'expanded' : railOpen ? 'expanded' : 'collapsed',
      setOpen,
      toggle,
      isMobile,
      isTablet,
      openMobile,
      setOpenMobile,
      accentSafe,
      accentIcons,
      rs,
    }),
    [railOpen, setOpen, toggle, isMobile, isTablet, openMobile, accentSafe, accentIcons, rs],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

/**
 * Minimal `asChild` slot — merges this component's props onto its single child
 * element. Replaces `@radix-ui/react-slot` so no Radix dependency leaks into
 * `@vitness/layouts`.
 */
type SlotProps = HTMLAttributes<HTMLElement> & { children: ReactNode };

const Slot = forwardRef<HTMLElement, SlotProps>(function Slot(
  { children, ...props },
  ref,
) {
  if (!isValidElement(children)) return null;
  const child = children as React.ReactElement<Record<string, unknown>>;
  return cloneElement(child, {
    ...props,
    ...child.props,
    ref,
    style: {
      ...(props.style as CSSProperties | undefined),
      ...(child.props.style as CSSProperties | undefined),
    },
  });
});

export interface SidebarShellProps extends HTMLAttributes<HTMLElement> {
  /** Accessible name for the navigation landmark. */
  'aria-label'?: string;
  width?: number;
  collapsedWidth?: number;
  children: ReactNode;
}

/**
 * The sidebar panel itself — a sticky, full-height navigation landmark whose
 * width animates between expanded and icon-collapsed states. Exposes
 * `data-state` (`expanded` | `collapsed`) for state-driven styling and tests.
 */
export const SidebarShell = forwardRef<HTMLElement, SidebarShellProps>(
  function SidebarShell(
    { children, width = EXPANDED_WIDTH, collapsedWidth = ICON_WIDTH, style, ...props },
    ref,
  ) {
    const { state, isMobile, openMobile, setOpenMobile } = useSidebar();

    // Mobile: the rail becomes an off-canvas drawer (backdrop + slide-in panel),
    // shadcn's built-in responsive behaviour. Always full width when open.
    if (isMobile) {
      return (
        <>
          <div
            aria-hidden="true"
            onClick={() => setOpenMobile(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              background: 'rgba(0,0,0,0.5)',
              opacity: openMobile ? 1 : 0,
              pointerEvents: openMobile ? 'auto' : 'none',
              transition: 'opacity 0.2s ease',
            }}
          />
          <nav
            ref={ref}
            data-state={openMobile ? 'expanded' : 'collapsed'}
            data-mobile="true"
            data-sidebar="shell"
            aria-hidden={!openMobile}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 51,
              width,
              // Divided by the interface scale for the same reason the inline
              // rail's height is — see the note below. An undivided 85vw is
              // 85% of the *unzoomed* window, i.e. wider than the screen once
              // the scale is above ~1.17.
              maxWidth: vp('85vw'),
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--vn-surface-raised)',
              borderRight: '1px solid var(--vn-border-default)',
              transform: openMobile ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.22s cubic-bezier(.4,0,.2,1)',
              overflow: 'hidden',
              ...style,
            }}
            {...props}
          >
            {children}
          </nav>
        </>
      );
    }

    const collapsed = state === 'collapsed';
    const currentWidth = collapsed ? collapsedWidth : width;

    return (
      <nav
        ref={ref}
        data-state={state}
        data-sidebar="shell"
        style={{
          width: currentWidth,
          minWidth: currentWidth,
          // Not a bare 100dvh: under a CSS `zoom` on <html> (the app's
          // interface-scale control) viewport units are not divided by the
          // zoom, so it lays out taller than the window and SidebarContent
          // grows a scrollbar. `vp()` applies the correction.
          height: vp('100dvh'),
          position: 'sticky',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--vn-surface-raised)',
          borderRight: '1px solid var(--vn-border-default)',
          transition: 'width 0.2s ease, min-width 0.2s ease',
          overflow: 'hidden',
          ...style,
        }}
        {...props}
      >
        {children}
      </nav>
    );
  },
);

export interface SidebarHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Top branding / header slot. Centers its content when collapsed. */
export function SidebarHeader({ children, style, ...props }: SidebarHeaderProps) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  return (
    <div
      data-sidebar="header"
      style={{
        padding: collapsed ? '12px 0' : '12px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        minHeight: 52,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--vn-font-display)',
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--vn-text-primary)',
        transition: 'padding 0.2s ease',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export interface SidebarContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Scrollable region that holds the sidebar groups/menus. */
export function SidebarContent({ children, style, ...props }: SidebarContentProps) {
  return (
    <div
      data-sidebar="content"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        padding: '0 8px',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export interface SidebarFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Bottom slot, divided from content with a top border. */
export function SidebarFooter({ children, style, ...props }: SidebarFooterProps) {
  return (
    <div
      data-sidebar="footer"
      style={{
        borderTop: '1px solid var(--vn-border-default)',
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export interface SidebarGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** Accessible name for the group landmark; defaults to the label text. */
  label?: string;
  children: ReactNode;
}

interface SidebarGroupContextValue {
  labelId?: string;
  setLabelId: (id: string) => void;
}

const SidebarGroupContext = createContext<SidebarGroupContextValue | null>(null);

/**
 * A labelled grouping of menu items. Renders `role="group"` wired to its
 * {@link SidebarGroupLabel} via `aria-labelledby` for assistive tech.
 */
export function SidebarGroup({ children, style, ...props }: SidebarGroupProps) {
  const { state } = useSidebar();
  const [labelId, setLabelId] = useState<string | undefined>(undefined);
  const ctx = useMemo<SidebarGroupContextValue>(
    () => ({ labelId, setLabelId }),
    [labelId],
  );

  return (
    <SidebarGroupContext.Provider value={ctx}>
      <div
        role="group"
        aria-labelledby={labelId}
        data-sidebar="group"
        // In icon mode the group labels are hidden, so the larger inter-group
        // gap reads as arbitrary — tighten it for an even column of icons.
        style={{ marginTop: state === 'collapsed' ? 4 : 10, ...style }}
        {...props}
      >
        {children}
      </div>
    </SidebarGroupContext.Provider>
  );
}

let groupLabelSeq = 0;

export interface SidebarGroupLabelProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Mono, uppercase section label. Hidden in collapsed icon mode. */
export function SidebarGroupLabel({ children, style, ...props }: SidebarGroupLabelProps) {
  const { state } = useSidebar();
  const groupCtx = useContext(SidebarGroupContext);
  const [id] = useState(() => `vn-sidebar-group-${groupLabelSeq++}`);

  useEffect(() => {
    groupCtx?.setLabelId(id);
  }, [groupCtx, id]);

  if (state === 'collapsed') return null;

  return (
    <div
      id={id}
      data-sidebar="group-label"
      style={{
        padding: '5px 8px',
        fontFamily: 'var(--vn-font-mono)',
        fontSize: 9,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: 'var(--vn-text-disabled)',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export interface SidebarMenuProps extends HTMLAttributes<HTMLUListElement> {
  children: ReactNode;
}

/** Unordered list wrapper for menu items. */
export function SidebarMenu({ children, style, ...props }: SidebarMenuProps) {
  return (
    <ul
      data-sidebar="menu"
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...style,
      }}
      {...props}
    >
      {children}
    </ul>
  );
}

export interface SidebarMenuItemProps extends HTMLAttributes<HTMLLIElement> {
  children: ReactNode;
}

/** A single list item slot. Centers its (shrink-wrapped) button in icon mode. */
export function SidebarMenuItem({ children, style, ...props }: SidebarMenuItemProps) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  return (
    <li
      data-sidebar="menu-item"
      style={{
        listStyle: 'none',
        ...(collapsed ? { display: 'flex', justifyContent: 'center' } : null),
        ...style,
      }}
      {...props}
    >
      {children}
    </li>
  );
}

export interface SidebarMenuButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Leading icon component. */
  icon?: ComponentType<LucideProps>;
  /** Whether this item represents the current page. */
  isActive?: boolean;
  /** Render the provided child element instead of a `<button>` (e.g. an anchor). */
  asChild?: boolean;
  /** Tooltip label override for collapsed mode (defaults to the text content). */
  tooltip?: string;
  /** Trailing status dot — operational (green), degraded (amber), or down (red). */
  status?: 'operational' | 'degraded' | 'down';
  /** Trailing badge text (e.g. `BETA`), rendered as a small accent pill. */
  badge?: string;
  children: ReactNode;
}

const STATUS_COLOR: Record<NonNullable<SidebarMenuButtonProps['status']>, string> = {
  operational: 'var(--vn-success)',
  degraded: 'var(--vn-warning)',
  down: 'var(--vn-danger)',
};

/**
 * Interactive nav entry. Active items render the Vitness accent treatment
 * (accent text + translucent accent background) and expose
 * `aria-current="page"`. When collapsed, the label is hidden and the button is
 * wrapped in the shared {@link Tooltip} showing the accessible name. Optional
 * trailing `status` dot / `badge` pill sit at the end of the row.
 */
export const SidebarMenuButton = forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  function SidebarMenuButton(
    { icon: Icon, isActive = false, asChild = false, tooltip, status, badge, children, style, ...props },
    ref,
  ) {
    const { state, accentSafe, accentIcons, rs } = useSidebar();
    const collapsed = state === 'collapsed';
    const textLabel = typeof children === 'string' ? children : undefined;
    const accessibleName = tooltip ?? textLabel;

    const baseStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      gap: 11,
      padding: collapsed ? '8px' : '7px 9px',
      borderRadius: 6,
      border: 'none',
      width: '100%',
      textAlign: 'left',
      fontFamily: 'var(--vn-font-body)',
      fontSize: 13,
      fontWeight: isActive ? 600 : 400,
      color: isActive ? accentSafe : 'var(--vn-text-secondary)',
      background: isActive ? `rgba(${rs},0.08)` : 'transparent',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textDecoration: 'none',
      transition: 'background 0.12s ease, color 0.12s ease',
      ...style,
    };

    const content = (
      <>
        {Icon && (
          <Icon
            size={16}
            color={isActive || accentIcons ? accentSafe : 'var(--vn-text-muted)'}
            strokeWidth={1.8}
          />
        )}
        {!collapsed && (
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
        )}
        {!collapsed && badge && (
          <span
            style={{
              flexShrink: 0,
              fontFamily: 'var(--vn-font-mono)',
              fontSize: 8,
              fontWeight: 600,
              color: '#000',
              background: `rgb(${rs})`,
              padding: '1px 5px',
              borderRadius: 3,
              letterSpacing: '0.02em',
            }}
          >
            {badge}
          </span>
        )}
        {!collapsed && status && (
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: STATUS_COLOR[status],
            }}
          />
        )}
      </>
    );

    // When collapsed the text label is visually hidden, so expose the name to
    // assistive tech via aria-label — independent of the (hover-only) tooltip.
    const commonProps = {
      'data-sidebar': 'menu-button',
      'data-active': isActive ? '' : undefined,
      'aria-current': isActive ? ('page' as const) : undefined,
      'aria-label': collapsed ? accessibleName : undefined,
      style: baseStyle,
    };

    const button = asChild ? (
      <Slot {...commonProps} {...props}>
        {children}
      </Slot>
    ) : (
      <button ref={ref} type="button" {...commonProps} {...props}>
        {content}
      </button>
    );

    if (collapsed && !asChild && accessibleName) {
      return (
        <Tooltip content={accessibleName} side="right">
          {button}
        </Tooltip>
      );
    }

    return button;
  },
);

export interface SidebarTriggerProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'onClick'> {
  children?: ReactNode;
}

/**
 * Button that toggles the sidebar. Reflects expanded state via
 * `aria-expanded`, carries an accessible "Toggle sidebar" label, and announces
 * the Ctrl/Cmd+B shortcut through its title.
 */
export const SidebarTrigger = forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  function SidebarTrigger({ children, style, ...props }, ref) {
    const { open, toggle, rs } = useSidebar();
    return (
      <button
        ref={ref}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label="Toggle sidebar"
        title="Toggle sidebar (Ctrl/Cmd+B)"
        data-sidebar="trigger"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          marginLeft: -6,
          borderRadius: 6,
          border: 'none',
          background: 'transparent',
          color: 'var(--vn-text-secondary)',
          cursor: 'pointer',
          transition: 'background 0.12s ease, color 0.12s ease',
          ...style,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = `rgba(${rs},0.08)`;
          e.currentTarget.style.color = 'var(--vn-text-primary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--vn-text-secondary)';
        }}
        {...props}
      >
        {children ?? <PanelLeft size={17} strokeWidth={1.8} />}
      </button>
    );
  },
);

export type SidebarRailProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'onClick'
>;

/**
 * Thin clickable rail along the panel's trailing edge — a hit target for
 * toggling collapse without a visible button, matching shadcn's rail affordance.
 */
export const SidebarRail = forwardRef<HTMLButtonElement, SidebarRailProps>(
  function SidebarRail({ style, ...props }, ref) {
    const { toggle, rs } = useSidebar();
    return (
      <button
        ref={ref}
        type="button"
        onClick={toggle}
        aria-hidden="true"
        tabIndex={-1}
        data-sidebar="rail"
        style={{
          position: 'absolute',
          insetBlock: 0,
          right: 0,
          width: 12,
          transform: 'translateX(50%)',
          border: 'none',
          background: 'transparent',
          cursor: 'col-resize',
          ...style,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = `linear-gradient(to right, transparent, rgba(${rs},0.16))`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
        {...props}
      />
    );
  },
);

export interface SidebarMenuSubProps extends HTMLAttributes<HTMLUListElement> {
  children: ReactNode;
}

/**
 * Nested list of sub-items, drawn with the shadcn-style vertical guide: a left
 * border running the height of the group, indented to sit under the parent
 * icon. Hidden in collapsed icon mode. Pair with {@link SidebarCollapsible}.
 */
export function SidebarMenuSub({ children, style, ...props }: SidebarMenuSubProps) {
  const { state } = useSidebar();
  if (state === 'collapsed') return null;
  return (
    <ul
      data-sidebar="menu-sub"
      style={{
        listStyle: 'none',
        margin: '1px 0 2px',
        marginLeft: 17,
        padding: '2px 0 2px 11px',
        borderLeft: '1px solid var(--vn-border-default)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...style,
      }}
      {...props}
    >
      {children}
    </ul>
  );
}

export interface SidebarMenuSubItemProps extends HTMLAttributes<HTMLLIElement> {
  children: ReactNode;
}

/** A single sub-list item slot. */
export function SidebarMenuSubItem({ children, style, ...props }: SidebarMenuSubItemProps) {
  return (
    <li data-sidebar="menu-sub-item" style={{ listStyle: 'none', ...style }} {...props}>
      {children}
    </li>
  );
}

export interface SidebarMenuSubButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Whether this sub-item represents the current page. */
  isActive?: boolean;
  /** Render the provided child element instead of a `<button>` (e.g. an anchor). */
  asChild?: boolean;
  children: ReactNode;
}

/**
 * Interactive sub-item. Text-only (no leading icon) to match the shadcn nested
 * style; active items take the accent text + translucent accent background.
 */
export const SidebarMenuSubButton = forwardRef<HTMLButtonElement, SidebarMenuSubButtonProps>(
  function SidebarMenuSubButton({ isActive = false, asChild = false, children, style, ...props }, ref) {
    const { accentSafe, rs } = useSidebar();
    const baseStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 9px',
      borderRadius: 6,
      border: 'none',
      width: '100%',
      fontFamily: 'var(--vn-font-body)',
      fontSize: 13,
      fontWeight: isActive ? 600 : 400,
      color: isActive ? accentSafe : 'var(--vn-text-secondary)',
      background: isActive ? `rgba(${rs},0.08)` : 'transparent',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textDecoration: 'none',
      transition: 'background 0.12s ease, color 0.12s ease',
      ...style,
    };

    const commonProps = {
      'data-sidebar': 'menu-sub-button',
      'data-active': isActive ? '' : undefined,
      'aria-current': isActive ? ('page' as const) : undefined,
      style: baseStyle,
    };

    if (asChild) {
      return (
        <Slot {...commonProps} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button ref={ref} type="button" {...commonProps} {...props}>
        {children}
      </button>
    );
  },
);

export interface SidebarCollapsibleProps {
  /** Leading icon for the trigger row. */
  icon?: ComponentType<LucideProps>;
  /** Trigger label. */
  label: ReactNode;
  /** A descendant is active — keeps the group open and accents the trigger. */
  isActive?: boolean;
  /** Initial open state when no descendant is active. Defaults to `false`. */
  defaultOpen?: boolean;
  /** Tooltip label for collapsed icon mode (defaults to the label text). */
  tooltip?: string;
  /** The {@link SidebarMenuSub} (and its items) revealed when open. */
  children: ReactNode;
}

/**
 * A collapsible nav group: a trigger row (icon + label + rotating chevron) that
 * expands to reveal a {@link SidebarMenuSub}. Open state is owned internally and
 * auto-opens whenever a descendant becomes active. In collapsed icon mode the
 * trigger renders icon-only with a tooltip and the sub-list is hidden.
 */
export function SidebarCollapsible({
  icon: Icon,
  label,
  isActive = false,
  defaultOpen = false,
  tooltip,
  children,
}: SidebarCollapsibleProps) {
  const { state, accentSafe, accentIcons, rs } = useSidebar();
  const collapsed = state === 'collapsed';
  const [open, setOpen] = useState(defaultOpen || isActive);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  const accessibleName = tooltip ?? (typeof label === 'string' ? label : undefined);

  const triggerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: 11,
    padding: collapsed ? '8px' : '7px 9px',
    borderRadius: 6,
    border: 'none',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'var(--vn-font-body)',
    fontSize: 13,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? accentSafe : 'var(--vn-text-secondary)',
    background: isActive && collapsed ? `rgba(${rs},0.08)` : 'transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    transition: 'background 0.12s ease, color 0.12s ease',
  };

  const trigger = (
    <button
      type="button"
      onClick={() => !collapsed && setOpen((prev) => !prev)}
      aria-expanded={collapsed ? undefined : open}
      aria-label={collapsed ? accessibleName : undefined}
      data-sidebar="collapsible-trigger"
      style={triggerStyle}
    >
      {Icon && (
        <Icon
          size={16}
          color={isActive || accentIcons ? accentSafe : 'var(--vn-text-muted)'}
          strokeWidth={1.8}
        />
      )}
      {!collapsed && (
        <>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
          <ChevronRight
            size={14}
            strokeWidth={1.8}
            color="var(--vn-text-muted)"
            style={{
              flexShrink: 0,
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease',
            }}
          />
        </>
      )}
    </button>
  );

  return (
    <li
      data-sidebar="collapsible"
      style={{
        listStyle: 'none',
        ...(collapsed ? { display: 'flex', justifyContent: 'center' } : null),
      }}
    >
      {collapsed && accessibleName ? (
        <Tooltip content={accessibleName} side="right">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      {!collapsed && open && children}
    </li>
  );
}
